import React, { useState } from 'react';
import { Edit, Trash2, AlertCircle } from "lucide-react";
import { useTheme } from '../../components/Theme/ThemeProvider';
import EditProducts from './EditProducts';
import { displayNGNCurrency } from '../../helpers/displayCurrency';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { authFetch } from '../../helpers/authFetch';

function ProductCard({ data, fetchData }) {
    const { colors } = useTheme();
    const [editItems, setEditItems] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteProduct = async () => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete "${data?.productName}"? This action cannot be undone.`
        );

        if (!confirmDelete) return;

        try {
            setIsDeleting(true);
            
            const response = await authFetch(`${summaryApi.deleteProduct.url}/${data._id}`, {
                method: summaryApi.deleteProduct.method,
                
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            const responseData = await response.json();

            if (responseData.success) {
                toast.success(responseData.message);
                fetchData();
            } else {
                toast.error(responseData.message);
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Failed to delete product");
        } finally {
            setIsDeleting(false);
        }
    };

    const discountPercentage = data.productDiscount && data.productPrice 
        ? Math.round((data.productDiscount / data.productPrice) * 100)
        : 0;

    return (
        <>
            <div 
                className="rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group"
                style={{
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`,
                }}
            >
                {/* Product Image */}
                <div 
                    className="aspect-square relative overflow-hidden"
                    style={{ backgroundColor: colors.bg }}
                >
                    <img
                        src={data?.productImage[0]}
                        alt={data.productName}
                        className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-300"
                    />
                    {discountPercentage > 0 && (
                        <div 
                            className="absolute top-3 right-3 px-3 py-1 rounded-full text-white text-xs font-bold"
                            style={{ backgroundColor: colors.accent }}
                        >
                            -{discountPercentage}%
                        </div>
                    )}
                </div>

                {/* Product Details */}
                <div className="p-4 space-y-3">
                    <h3 
                        className="text-sm font-semibold line-clamp-2 h-10 transition-colors group-hover:opacity-80"
                        style={{ color: colors.text }}
                    >
                        {data?.productName}
                    </h3>

                    <div className="space-y-1">
                        <p 
                            className="text-xs"
                            style={{ color: colors.textSecondary }}
                        >
                            {data?.productBrand}
                        </p>
                        <div className="flex items-center justify-between">
                            <span 
                                className="text-lg font-bold"
                                style={{ color: colors.primary }}
                            >
                                {displayNGNCurrency(data.productPrice)}
                            </span>
                            {data.productDiscount > 0 && (
                                <span 
                                    className="text-xs line-through"
                                    style={{ color: colors.textSecondary }}
                                >
                                    {displayNGNCurrency(data.productPrice + data.productDiscount)}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center space-x-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                        <button
                            onClick={() => setEditItems(true)}
                            className="flex-1 inline-flex items-center justify-center space-x-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all hover:scale-105"
                            style={{
                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                            }}
                        >
                            <Edit className="w-4 h-4" />
                            <span>Edit</span>
                        </button>
                        <button
                            onClick={handleDeleteProduct}
                            disabled={isDeleting}
                            className="px-4 py-2 text-white rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ backgroundColor: colors.accent }}
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {editItems && (
                <EditProducts
                    fetchData={fetchData}
                    productData={data}
                    onClose={() => setEditItems(false)}
                />
            )}
        </>
    );
}

export default ProductCard;